import React, { useEffect, useRef, useState } from "react";
import {
  calculateQuoteWindow,
  canonicalQuoteWindowJson,
  normalizeQuoteWindowSymbol,
  parseQuoteWindowAt,
  validateQuoteWindowInput,
} from "@shared/radarQuoteWindow";
import type {
  QuoteWindowComparison,
  QuoteWindowPacket,
  QuoteWindowSample,
  QuoteWindowSlot,
} from "@shared/radarQuoteWindow";
import { COLORS } from "./radarFormat";

const MAX_PACKET_BYTES = 65_536;

function normalizeSymbol(value: string): string | null {
  return normalizeQuoteWindowSymbol(value);
}

function parseUtcMinute(value: string, nowMs: number): number | null {
  return parseQuoteWindowAt(value, nowMs);
}

function utcLabel(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isSafeInteger(ms)) return "not available";
  return new Date(ms).toISOString();
}

function rawNumber(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "not available";
}

function reasonLabel(reason: string | null): string {
  if (!reason) return "Available";
  const labels: Record<string, string> = {
    "not-yet-complete": "Target bucket was not complete at the read clock",
    "missing-row": "No retained row for this exact bucket",
    "invalid-observation-clock": "Queue clock is outside the allowed bucket/read window",
    "missing-quote": "Bid, ask, or provider timestamp is missing",
    "invalid-quote": "Bid or ask is non-positive, or bid exceeds ask",
    "invalid-quote-clock": "Provider timestamp is outside its bucket or after queue time",
    "baseline-unavailable": "Baseline quote is unavailable",
    "invalid-sample-order": "Provider timestamps are not strictly increasing",
    "calculation-unavailable": "A finite comparison could not be calculated",
  };
  return labels[reason] ?? reason.replaceAll("-", " ");
}

/** Validate identity and recompute the deterministic result before rendering. */
function isQuoteWindowPacket(value: unknown, expectedSymbol: string, expectedAt: string): value is QuoteWindowPacket {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const packet = value as Partial<QuoteWindowPacket> & Record<string, unknown>;
  if (Object.keys(packet).sort().join(",") !== "digest,input,result,version") return false;
  if (packet.version !== "tsm-radar-quote-window-export-v1" || typeof packet.digest !== "string" || !/^[a-f0-9]{64}$/.test(packet.digest)) return false;
  try {
    const input = validateQuoteWindowInput(packet.input);
    if (input.symbol !== expectedSymbol || input.at !== expectedAt) return false;
    const result = calculateQuoteWindow(input);
    return canonicalQuoteWindowJson(packet.result) === canonicalQuoteWindowJson(result);
  } catch {
    return false;
  }
}

function SourceValue({ source, field }: { source: QuoteWindowSample | null; field: "bid" | "ask" }) {
  return <>{source ? rawNumber(source[field]) : "—"}</>;
}

function QuoteSampleTable({ slots }: { slots: QuoteWindowSlot[] }) {
  return (
    <div
      className="radar-quote-window-scroll"
      role="region"
      aria-label="Retained quote samples"
      tabIndex={0}
      style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${COLORS.border}` }}
    >
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 980, fontSize: 11 }}>
        <thead>
          <tr>
            {["Offset", "Bucket UTC", "State / reason", "Bid (USDT)", "Ask (USDT)", "Midpoint", "Published / tier", "Recorded samples", "Provider clock", "Queue clock"].map((label) => (
              <th key={label} scope="col" style={headCell}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slots.map((slot) => {
            const source = slot.source;
            return (
              <tr key={slot.offsetMinutes}>
                <th scope="row" style={bodyCell}>+{slot.offsetMinutes}m</th>
                <td style={bodyCell}>{utcLabel(slot.bucketStartMs)}</td>
                <td style={{ ...bodyCell, color: slot.status === "available" ? COLORS.good : COLORS.warn }}>
                  <span>{slot.status === "available" ? "Available" : "Unavailable"}</span>
                  {slot.reason && <div>{reasonLabel(slot.reason)}</div>}
                </td>
                <td style={bodyCell}><SourceValue source={source} field="bid" /></td>
                <td style={bodyCell}><SourceValue source={source} field="ask" /></td>
                <td style={bodyCell}>{rawNumber(slot.midpoint)}</td>
                <td style={bodyCell}>{source ? `${source.published ? "Published" : "Grace-only"} · ${source.dataQuality.toUpperCase()}` : "—"}</td>
                <td style={bodyCell}>{source ? source.sampleCount === null ? "not reported" : source.sampleCount : "—"}</td>
                <td style={bodyCell}>{source ? utcLabel(source.quoteTimestampMs) : "—"}</td>
                <td style={bodyCell}>{source ? utcLabel(source.observedAtMs) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ComparisonTable({ comparisons }: { comparisons: QuoteWindowComparison[] }) {
  return (
    <div
      className="radar-quote-window-scroll"
      role="region"
      aria-label="Midpoint comparisons from baseline"
      tabIndex={0}
      style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", border: `1px solid ${COLORS.border}` }}
    >
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560, fontSize: 11 }}>
        <thead>
          <tr>{["Sample bucket", "Comparison", "Midpoint change", "Actual provider elapsed"].map((label) => <th key={label} scope="col" style={headCell}>{label}</th>)}</tr>
        </thead>
        <tbody>
          {comparisons.map((comparison) => (
            <tr key={comparison.offsetMinutes}>
              <th scope="row" style={bodyCell}>+{comparison.offsetMinutes}m</th>
              <td style={{ ...bodyCell, color: comparison.status === "available" ? COLORS.good : COLORS.warn }}>
                {comparison.status === "available" ? "Available" : reasonLabel(comparison.reason)}
              </td>
              <td style={bodyCell}>{comparison.midpointChangeBps === null ? "not available" : `${comparison.midpointChangeBps.toFixed(4)} bps`}</td>
              <td style={bodyCell}>{comparison.elapsedSampleMs === null ? "not available" : `${comparison.elapsedSampleMs.toLocaleString()} ms`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const headCell: React.CSSProperties = {
  color: COLORS.dim, textAlign: "left", fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase",
  fontWeight: 700, padding: "7px 8px", borderBottom: `1px solid ${COLORS.border}`, whiteSpace: "nowrap",
};

const bodyCell: React.CSSProperties = {
  color: COLORS.text, textAlign: "left", padding: "7px 8px", borderBottom: `1px solid ${COLORS.border}`,
  verticalAlign: "top", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap",
};

export default function RadarQuoteWindow({
  prefillSymbol,
  prefillRevision = 0,
}: { prefillSymbol?: string | null; prefillRevision?: number }) {
  const [symbol, setSymbol] = useState("");
  const [at, setAt] = useState("");
  const [packet, setPacket] = useState<QuoteWindowPacket | null>(null);
  const [packetText, setPacketText] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    if (prefillSymbol) setSymbol(prefillSymbol);
  }, [prefillSymbol, prefillRevision]);

  useEffect(() => () => {
    requestId.current += 1;
    controller.current?.abort();
  }, []);

  async function inspect() {
    controller.current?.abort();
    const activeRequest = ++requestId.current;
    const canonicalSymbol = normalizeSymbol(symbol);
    const atMs = parseUtcMinute(at, Date.now());
    if (!canonicalSymbol) {
      setPacket(null);
      setPacketText("");
      setMessage("Enter a valid Bitget USDT-futures contract symbol.");
      setLoading(false);
      return;
    }
    if (atMs === null) {
      setPacket(null);
      setPacketText("");
      setMessage("Enter a completed canonical UTC minute ending in :00.000Z.");
      setLoading(false);
      return;
    }

    const activeController = new AbortController();
    controller.current = activeController;
    const canonicalAt = new Date(atMs).toISOString();
    setSymbol(canonicalSymbol);
    setPacket(null);
    setPacketText("");
    setMessage("");
    setLoading(true);

    try {
      const response = await fetch(
        `/api/market-radar/${encodeURIComponent(canonicalSymbol)}/quote-window?at=${encodeURIComponent(canonicalAt)}`,
        { method: "GET", signal: activeController.signal, cache: "no-store" },
      );
      const responseText = await response.text();
      if (activeRequest !== requestId.current) return;
      if (!response.ok) {
        let detail = "Retained quote samples are unavailable.";
        try {
          const body = JSON.parse(responseText) as { error?: { message?: unknown } };
          if (typeof body.error?.message === "string") detail = body.error.message;
        } catch { /* Keep the safe local message for non-JSON errors. */ }
        setMessage(detail);
        return;
      }
      if (new TextEncoder().encode(responseText).byteLength > MAX_PACKET_BYTES) {
        setMessage("The returned quote packet exceeds the supported size limit.");
        return;
      }
      let value: unknown;
      try {
        value = JSON.parse(responseText);
      } catch {
        setMessage("The quote response was not valid JSON.");
        return;
      }
      if (!isQuoteWindowPacket(value, canonicalSymbol, canonicalAt)) {
        setMessage("The quote response did not match the retained packet contract.");
        return;
      }
      setPacket(value);
      setPacketText(responseText);
    } catch (error) {
      if (activeRequest !== requestId.current || activeController.signal.aborted) return;
      setMessage(error instanceof Error ? error.message : "The quote request failed.");
    } finally {
      if (activeRequest === requestId.current) setLoading(false);
    }
  }

  function inspectOnEnter(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void inspect();
  }

  function downloadPacket() {
    if (!packet || !packetText) return;
    const blob = new Blob([packetText], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `radar-quote-window-${packet.input.symbol}-${packet.input.at.replaceAll(":", "-")}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section
      aria-labelledby="radar-quote-window-title"
      data-testid="radar-quote-window"
      style={{ border: `1px solid ${COLORS.border}`, background: "#0d0d0d", padding: 12, margin: "0 0 14px" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 id="radar-quote-window-title" style={{ color: COLORS.accent, fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", margin: "0 0 4px" }}>
            RETAINED QUOTE WINDOW
          </h2>
          <p style={{ color: COLORS.dim, fontSize: 10, lineHeight: 1.5, margin: 0, maxWidth: 900 }}>
            Inspect exact stored Bitget USDT-futures ticker samples at baseline and +1, +3, +5, +10 and +15 minutes. Each row is one retained sample bucket; missing minutes between these buckets are not checked. A midpoint change is descriptive and does not establish an executable return, a signal outcome, or causation.
          </p>
        </div>
        {packet && (
          <button type="button" onClick={downloadPacket} style={buttonStyle}>
            DOWNLOAD INSPECTED PACKET
          </button>
        )}
      </div>

      <fieldset aria-label="Retained quote window request" style={{ border: 0, minWidth: 0, padding: 0, margin: "11px 0 0" }}>
        <legend style={{ color: COLORS.fainter, fontSize: 9, letterSpacing: "0.07em", textTransform: "uppercase", padding: 0, marginBottom: 6 }}>
          Request inputs · UTC
        </legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 9, alignItems: "end" }}>
          <label style={labelStyle}>
            Contract
            <input
              aria-label="Quote window contract"
              autoComplete="off"
              spellCheck={false}
              value={symbol}
              onChange={(event) => setSymbol(event.target.value)}
              onKeyDown={inspectOnEnter}
              placeholder="AAVEUSDT"
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            UTC minute (canonical)
            <input
              aria-label="Quote window UTC minute"
              autoComplete="off"
              spellCheck={false}
              value={at}
              onChange={(event) => setAt(event.target.value)}
              onKeyDown={inspectOnEnter}
              placeholder="2026-10-11T00:00:00.000Z"
              style={inputStyle}
            />
          </label>
          <button type="button" aria-label="Inspect retained quote window" onClick={() => void inspect()} style={{ ...buttonStyle, opacity: loading ? 0.8 : 1 }}>
            {loading ? "INSPECT NEWER WINDOW" : "INSPECT RETAINED WINDOW"}
          </button>
        </div>
      </fieldset>
      <div style={{ color: COLORS.fainter, fontSize: 9, marginTop: 5 }}>
        Times are UTC. The minute must be complete. This form reads its entered contract and time only; it does not depend on the current board or detail panel.
      </div>
      {message && <div role="status" aria-live="polite" style={{ color: COLORS.warn, fontSize: 11, marginTop: 9 }}>{message}</div>}

      {packet && (
        <div style={{ marginTop: 12 }} data-testid="radar-quote-window-result">
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "baseline", marginBottom: 8, fontSize: 10 }}>
            <strong style={{ color: COLORS.text }}>{packet.input.symbol} · {packet.input.at}</strong>
            <span style={{ color: COLORS.accent }}>{packet.result.status.toUpperCase()} · {packet.result.comparisons.filter((item) => item.status === "available").length}/5 comparisons</span>
            <span style={{ color: COLORS.dim }}>Read clock: {packet.input.readAt}</span>
            <span style={{ color: COLORS.fainter, minWidth: 0, overflowWrap: "anywhere" }}>Packet digest: {packet.digest}</span>
          </div>
          <h3 style={subheadStyle}>Six exact source slots</h3>
          <QuoteSampleTable slots={packet.result.slots} />
          <h3 style={{ ...subheadStyle, marginTop: 12 }}>Five midpoint comparisons from baseline</h3>
          <ComparisonTable comparisons={packet.result.comparisons} />
          <details style={{ color: COLORS.fainter, fontSize: 10, marginTop: 9 }}>
            <summary style={{ cursor: "pointer", color: COLORS.dim }}>Method and source limits</summary>
            <ul style={{ margin: "5px 0 0", paddingLeft: 18, lineHeight: 1.5 }}>
              {packet.result.limitations.map((limitation, index) => <li key={`${index}-${limitation}`}>{limitation}</li>)}
            </ul>
          </details>
          <div style={{ color: COLORS.fainter, fontSize: 9, lineHeight: 1.5, marginTop: 8 }}>
            Provider timestamp is not proven to be the last bid/ask update. Queue time is sampled before durable batch persistence; it is not a receipt or proof of original public availability. “Published” means included in the recorded evaluation snapshot; “Grace-only” means the row was not admitted to that evaluation snapshot.
          </div>
        </div>
      )}
    </section>
  );
}

const labelStyle: React.CSSProperties = { display: "grid", gap: 4, color: COLORS.dim, fontSize: 10 };
const inputStyle: React.CSSProperties = {
  background: "#111", border: "1px solid #2a2a2a", color: COLORS.text, fontSize: 11,
  padding: "7px 8px", minWidth: 0, width: "100%", fontVariantNumeric: "tabular-nums",
};
const buttonStyle: React.CSSProperties = {
  background: "#15110b", border: `1px solid ${COLORS.accent}`, color: COLORS.accent,
  fontSize: 9, fontWeight: 800, letterSpacing: "0.05em", padding: "8px 10px", cursor: "pointer",
};
const subheadStyle: React.CSSProperties = {
  color: COLORS.dim, fontSize: 9, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", margin: "0 0 6px",
};
